import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";

import PlusIcon from '@rsuite/icons/Plus';
import TrashIcon from '@rsuite/icons/Trash';

import 'ckeditor5/ckeditor5.css';
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Button, Input, InputGroup, Table } from 'rsuite';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function TissChart({ data, reload }: any) {
    const { t } = useTranslation();
    let cookie = getCookies();

    const [columns, setColumns] = React.useState<any[]>(data.data?.columns || []);
    const [list, setList] = React.useState<any[]>(data.data?.list || []);
    const [rowText, setRowText] = React.useState<string>('');
    const [newColumnName, setNewColumnName] = React.useState<string>('');
    const [editingColumn, setEditingColumn] = React.useState<string | null>(null);
    const [editedColumnName, setEditedColumnName] = React.useState<string>('');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');

    const AddRow = () => {
        if (!rowText.trim()) {
            Swal.fire(t('Please enter valid text.'));
            return;
        }

        const newRow = columns.reduce(
            (acc, col) => ({ ...acc, [col.key]: col.key === 'text' ? rowText.trim() : '' }),
            { arrange: list.length + 1 }
        );

        setList([...list, newRow]);
        setRowText('');
    };

    const AddColumn = () => {
        if (!newColumnName.trim()) {
            Swal.fire(t('Please enter a valid column name.'));
            return;
        }

        const columnKey = newColumnName.toLowerCase().replace(/\s+/g, '_');
        if (columns.find((col) => col.key === columnKey)) {
            Swal.fire(t('Column name already exists.'));
            return;
        }

        // Assign a default color to the new column
        setColumns([...columns, { key: columnKey, label: newColumnName, color: getRandomColor() }]);
        setList(list.map((row) => ({ ...row, [columnKey]: '' })));
        setNewColumnName('');
    };

    const editColumnName = (key: string, newName: string) => {
        setColumns(
            columns.map((col) => (col.key === key ? { ...col, label: newName } : col))
        );
        setEditingColumn(null);
        setEditedColumnName('');
    };

    const sortByArrange = () => {
        const sortedList = [...list].sort((a, b) =>
            sortOrder === 'asc' ? a.arrange - b.arrange : b.arrange - a.arrange
        );
        setList(sortedList);
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const save = async () => {
        try {
            const path = `${config.url}/v1/dashboard/component/texteditor/${data._id}`;

            await axios.put(path, { list, columns }, {
                headers: {
                    Authorization: `Bearer ${cookie['token']}`,
                },
            });

            Swal.fire(t('Save successful!'), '', 'success');
        } catch (error: any) {
            console.error('Error saving data:', error);
            Swal.fire(t('Error saving data.'), error.message || '', 'error');
        }
    };

    const editCell = (rowIndex: number, columnKey: string, value: string) => {
        const updatedList = list.map((row, idx) =>
            idx === rowIndex ? { ...row, [columnKey]: value } : row
        );
        setList(updatedList);
    };

    // Helper to generate random colors
    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    // Prepare chart data
    const chartData = list.map((row) => {
        const dataPoint: any = { name: `Row ${row.arrange}` };
        columns.forEach((col) => {
            if (col.key !== 'text' && row[col.key]) {
                dataPoint[col.label] = row[col.key];
            }
        });
        return dataPoint;
    });

    return (
        <div className="w-full p-4" dir="rtl">
            {/* Row Input */}
            <InputGroup inside>
                <Input
                    size={`xs`}
                    value={rowText}
                    onChange={(value) => setRowText(value)}
                    placeholder={t('Enter text')}
                />
                <InputGroup.Addon>
                    <Button onClick={AddRow}>
                        <PlusIcon />
                    </Button>
                </InputGroup.Addon>
            </InputGroup>

            {/* Column Input */}
            <InputGroup inside className="mt-4">
                <Input
                    size={`xs`}
                    value={newColumnName}
                    onChange={(value) => setNewColumnName(value)}
                    placeholder={t('Enter column name')}
                />
                <InputGroup.Addon>
                    <Button onClick={AddColumn}>{t('Add Column')}</Button>
                </InputGroup.Addon>
            </InputGroup>

            {/* Table */}
            <Table data={list} autoHeight className="mt-4">
                <Table.Column width={50} align="center">
                    <Table.HeaderCell>
                        <div className="flex items-center">
                            {t('#')}
                            <Button size="xs" onClick={sortByArrange} className="ml-2">
                                {sortOrder === 'asc' ? '↑' : '↓'}
                            </Button>
                        </div>
                    </Table.HeaderCell>
                    <Table.Cell dataKey="arrange" />
                </Table.Column>

                {columns.map((col) => (
                    <Table.Column key={col.key} flexGrow={1}>
                        <Table.HeaderCell>
                            {editingColumn === col.key ? (
                                <InputGroup inside>
                                    <Input
                                        size={`xs`}
                                        value={editedColumnName}
                                        onChange={(value) => setEditedColumnName(value)}
                                        placeholder={t('Enter name')}
                                    />
                                    <InputGroup.Addon>
                                        <Button
                                            onClick={() => editColumnName(col.key, editedColumnName)}
                                        >
                                            {t('Save')}
                                        </Button>
                                    </InputGroup.Addon>
                                </InputGroup>
                            ) : (
                                <div
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setEditingColumn(col.key);
                                        setEditedColumnName(col.label);
                                    }}
                                >
                                    {col.label}
                                </div>
                            )}
                        </Table.HeaderCell>
                        <Table.Cell>
                            {(rowData, rowIndex: any) => (
                                <Input
                                    size={`xs`}
                                    value={rowData[col.key] || ''}
                                    onChange={(value) => editCell(rowIndex, col.key, value)}
                                />
                            )}
                        </Table.Cell>
                    </Table.Column>
                ))}

                <Table.Column width={100} align="center">
                    <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>
                    <Table.Cell>
                        {(rowData, rowIndex: any) => (
                            <Button onClick={() => setList(list.filter((_, i) => i !== rowIndex))}>
                                <TrashIcon />
                            </Button>
                        )}
                    </Table.Cell>
                </Table.Column>
            </Table>

            <hr />

            {/* Chart Visualization */}
            <div className="mt-4">
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {columns
                            .filter((col) => col.key !== 'text')
                            .map((col) => (
                                <Bar
                                    key={col.key}
                                    dataKey={col.label}
                                    fill={col.color || getRandomColor()}
                                />
                            ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <Button onClick={save} className="mt-4">{t('Save')}</Button>
        </div>
    );
}
